import { useQuery } from '@tanstack/react-query';
import { type JSX, useSyncExternalStore } from 'react';

import { Checkbox } from '@/components/ui/checkbox.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { workspaceMembersQueryOptions } from '@/lib/api/workspaces.js';
import { titleWhenTruncated } from '@/lib/title-when-truncated.js';
import {
  getState as getIdentityState,
  subscribe as subscribeIdentity,
} from '@/store/identity-store.js';

/**
 * Multi-select of workspace members, sourced from the workspace directory (§18).
 *
 * It lives with the workspace feature rather than with either of the dialogs that use it, because both
 * of them ask the same question of the same directory: the conversation dialog picks the members a
 * `group` chat is shared with, the file dialog the members a `group` file is shared with. Pure and
 * controlled — the parent owns the selected id set — so it is straightforward to test in isolation.
 *
 * A row's two halves both give way, in proportion to how long each one is: the name took all the free
 * space and the address none of it, so once the dialog stopped growing to fit a long mailbox address
 * the name was squeezed down to its first letter while the address kept every pixel it asked for.
 */
export function WorkspaceMemberPicker({
  workspaceId,
  selectedIds,
  onToggle,
}: {
  workspaceId: string;
  selectedIds: ReadonlySet<string>;
  onToggle: (userId: string, selected: boolean) => void;
}): JSX.Element {
  const identity = useSyncExternalStore(
    subscribeIdentity,
    getIdentityState,
    getIdentityState,
  );
  const membersQuery = useQuery({
    ...workspaceMembersQueryOptions(workspaceId),
    enabled: Boolean(workspaceId),
  });

  if (membersQuery.isLoading) {
    return (
      <div className="flex flex-col gap-2" aria-busy>
        {[0, 1, 2].map((row) => (
          <Skeleton key={row} className="h-9 w-full rounded-md" />
        ))}
      </div>
    );
  }

  /**
   * A failed directory read is NOT an empty workspace. Reporting it as "no other
   * members" told the editor the opposite of the truth — there was nobody to pick —
   * and hid the retry they actually needed.
   */
  if (membersQuery.isError) {
    return (
      <p className="text-sm text-destructive">
        Couldn&apos;t load the workspace members. Close and reopen this dialog
        to try again.
      </p>
    );
  }

  const currentUserId = identity.identity?.userId ?? null;
  const members = (membersQuery.data ?? []).filter(
    (member) => member.userId !== currentUserId,
  );
  if (members.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No other members in this workspace.
      </p>
    );
  }

  return (
    <ul className="flex max-h-60 flex-col gap-0.5 overflow-y-auto">
      {members.map((member) => {
        const label = member.name ?? member.email ?? member.userId;
        const checked = selectedIds.has(member.userId);
        return (
          <li key={member.userId}>
            <label className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-accent">
              <Checkbox
                checked={checked}
                onCheckedChange={(next) =>
                  onToggle(member.userId, next === true)
                }
              />
              <span
                className="min-w-0 flex-auto truncate text-sm"
                onMouseEnter={titleWhenTruncated}
              >
                {label}
              </span>
              {member.email && member.name && (
                <span
                  className="min-w-0 flex-auto truncate text-right text-xs text-muted-foreground"
                  onMouseEnter={titleWhenTruncated}
                >
                  {member.email}
                </span>
              )}
            </label>
          </li>
        );
      })}
    </ul>
  );
}
